import { useCallback, useEffect } from 'react';
import { noop } from 'lodash-es';
import { useSpeechRecognition } from 'site-modules/shared/hooks/use-speech-recognition';
import { useInputCursorPosition } from 'site-modules/shared/hooks/use-input-cursor-position';

export function useChatInputHandlers({
  inputRef,
  inputValue = '',
  onInputChange,
  onInputClick = noop,
  onMicClick = noop,
  isSpeechEnabled = true,
}) {
  const { isListening, isSupported, isAllowed, toggleListening, stopListening, setTranscriptCallback } =
    useSpeechRecognition(isSpeechEnabled);
  const { insertTranscriptAtCursor, updateCursorFromInput, resetTranscriptTracking } = useInputCursorPosition(
    inputRef,
    inputValue,
    isListening
  );

  const handleTranscriptChange = useCallback(
    transcript => {
      const newValue = insertTranscriptAtCursor(inputValue || '', transcript);

      if (newValue !== null) {
        onInputChange(newValue);
      }
    },
    [inputValue, insertTranscriptAtCursor, onInputChange]
  );

  useEffect(() => {
    setTranscriptCallback(handleTranscriptChange);
  }, [handleTranscriptChange, setTranscriptCallback]);

  const handleInputChange = useCallback(
    value => {
      stopListening();
      onInputChange(value);
    },
    [onInputChange, stopListening]
  );

  const handleInputClick = useCallback(
    event => {
      updateCursorFromInput();
      onInputClick(event);
    },
    [onInputClick, updateCursorFromInput]
  );

  const handleToggleListening = useCallback(
    event => {
      onMicClick(event);

      if (!isListening) {
        updateCursorFromInput();
        resetTranscriptTracking();
      }
      toggleListening();
    },
    [isListening, onMicClick, resetTranscriptTracking, toggleListening, updateCursorFromInput]
  );

  return {
    isListening,
    isSupported,
    isAllowed,
    stopListening,
    handleInputChange,
    handleInputClick,
    handleToggleListening,
  };
}
